/**
 * Which edge of a box a press landed on — for a finger.
 *
 * The editor's resize handles are 10px squares centred on the box edge. A mouse hits a
 * 10px square; a finger does not, and every miss lands on the box itself, whose own
 * pointerdown MOVES it — so a phone user trying to widen a tile shoved it around instead,
 * twenty times. Rather than inflate the handles until they swallow a small tile, touch
 * asks this function where the press fell relative to the selected box and grabs the
 * matching edge if it fell within a finger's width of one, whichever side of the line.
 *
 * Pure geometry in CSS pixels, with no DOM: the canvas hands in the box's rect and the
 * pointer's client coordinates, and a check exercises it the way snap-grid exercises the
 * grid — with numbers, not a browser.
 */

/** The eight grips of a box, named by compass point. */
export type HandleId = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

/**
 * How far from an edge a finger may land and still take it, in CSS pixels, on EACH side
 * — 44px in all, the standard finger target, centred on the edge line rather than sitting
 * inside the box, so a finger aiming at the edge is right whether it lands a little in or
 * a little out.
 */
export const TOUCH_GRAB_PX = 22;

/**
 * How much of the box the inside half of a band may claim, per axis. 30% each side leaves
 * the middle 40% for moving, so a tile never becomes all edges: on a 30px-tall tile the
 * inside band shrinks to 9px, but the outside band is still the full 22px, and the tile
 * still has a middle to be dragged by. Under one half, or opposite edges could both match.
 */
export const GRAB_INSIDE_SHARE = 0.3;

export interface PxBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The edge or corner within reach of a press at (px, py), or null when the press is in
 * the middle of the box (a move) or too far outside it (not this box's business).
 */
export function handleAtPoint(
  box: PxBox,
  px: number,
  py: number,
  grab = TOUCH_GRAB_PX,
): HandleId | null {
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  if (px < box.left - grab || px > right + grab) return null;
  if (py < box.top - grab || py > bottom + grab) return null;

  const inX = Math.min(grab, box.width * GRAB_INSIDE_SHARE);
  const inY = Math.min(grab, box.height * GRAB_INSIDE_SHARE);
  const w = px < box.left + inX;
  const e = px > right - inX;
  const n = py < box.top + inY;
  const s = py > bottom - inY;

  const ns = n ? "n" : s ? "s" : "";
  const ew = w ? "w" : e ? "e" : "";
  const id = `${ns}${ew}`;
  return id ? (id as HandleId) : null;
}
